const AbstractLine = require('@/lib/Lines/AbstractLine');
const BigNumber = require('bignumber.js');
const Branch = require('@/lib/Branches/Branch');
const CartItemDiscount = require('@/libraries/DigiTickets/Offers/CartItemDiscount');
const Contact = require('@/lib/Contacts/Contact');
const ErrorBag = require('@/libraries/DigiTickets/Errors/ErrorBag');
const FieldInstanceFilter = require('@/lib/CustomFields/FieldInstanceFilter');
const ItemEditableStatus = require('@/lib/Orders/ItemEditableStatus');
const ItemInstance = require('@/libraries/DigiTickets/Orders/ItemInstance');
const ItemType = require('@/lib/Items/ItemType');
const mapItemTypeToModel = require('@/lib/Items/mapItemTypeToModel');
const Membership = require('@/lib/Memberships/Membership');
const MembershipDataset = require('@/lib/Memberships/MembershipDataset');
const MembershipPlan = require('@/lib/Memberships/MembershipPlan');
const moment = require('moment');
const Offer = require('@/lib/Offers/Offer');
const OptionSet = require('@/lib/ItemOptions/OptionSet');
const OrderLine = require('@/libraries/DigiTickets/Orders/OrderLine');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const PaymentPatterns = require('@/lib/PaymentMethods/PaymentPatterns');
const PriceAdjustmentCalculator = require('@/libraries/DigiTickets/PriceAdjustmentCalculator');
const Session = require('@/lib/Sessions/Session');
const SoldGiftVoucherData = require('@/lib/GiftVouchers/SoldGiftVoucherData');
const Ticket = require('@/lib/Tickets/Ticket');
const VariableDiscountsCollection = require('@/models/VariableDiscountsCollection');
const { calculateCartLineUiState } = require('@/lib/Cart/calculateCartLineUiState');
const { cleanCartLine } = require('@/libraries/DigiTickets/Cart/cartLineCleaning');
const { cloneShallow } = require('@/functions/clone');
const { convertItemOptionLinesToOptionSelections } = require('@/lib/ItemOptions');
const { getAllFieldGroupsFromLines, getAllFieldInstancesFromGroups, getHashOfFieldValues } = require('@/lib/CustomFields/fieldFunctions');
const { getPriceForOptionsOnLine } = require('@/lib/CustomFields');
const { md5Hash } = require('@/functions/hash');
const { toBool, toDate, toFloat, toInt, toNullableFloat, toNullableInt, toNullableString, toString } = require('@/functions/transform');

class CartLine extends AbstractLine {
    /**
     * @param {CartService} cart
     * @param {AbstractItem} item
     * @param {Branch} branch
     */
    constructor(cart, item, branch) {
        super();
        this.branch = branch;

        this.item = item;
        if (this.item) {
            this.price = this.item.getPrice();
        }

        /**
         * @deprecated
         * @private
         * Be careful - this is a CartService not a Cart!
         * TODO: Remove this coupling between CartItems and the Cart (PRO-972)
         * Generally if you want access to the CartService you should inject it into where you need it and not access
         * this property. This exists because the CartLine sometimes needs to tell CartService it needs to update after
         * some change to the line.
         *
         * @type {?CartService}
         */
        this.cart = null;

        /**
         * @type {number}
         */
        this.quantity = 0;

        /**
         * If this line came from an order that is being edited this property will always be the initial qty of the
         * line, as it was on the order. This value should not change after the line has been created.
         *
         * @type {number}
         */
        this.qtyBeforeAdjustment = 0;

        /**
         * @type {ItemInstance[]}
         */
        this.itemInstances = [];

        /**
         * If this line came from an original order (we are adjusting an order), and some qty has been removed from
         * the line, keep a backup of the removed item instances in case the qty is increased again later.
         * Note that this will only last while the line is being adjusted. Once the adjustment is saved we'll lose
         * those original instances.
         * Alternatively we could keep a list of all the original item instances instead of keeping track of the removed
         * ones, but it seems like a waste of memory to do it that way.
         *
         * @type {ItemInstance[]}
         */
        this.removedOriginalItemInstances = [];

        /**
         * How much of the quantity on this line is discounted?
         *
         * @type {number}
         */
        this.discountedQuantity = 0;

        this.quantityDisplay = this.quantity;

        /**
         * FIXME: Why doesn't this just use a ReturnReason model?
         *
         * @type {{returnToStock: ?boolean, reasonID: ?number}}
         */
        this.returnReason = {
            reasonID: null,
            description: null,
            returnToStock: this.item && this.item.itemType !== ItemType.PRODUCT ? true : null,
        };

        /**
         * An optional string to display below the item's name on the cart.
         *
         * @type {?string}
         */
        this.subtitle = null;

        /**
         * An offer that has manually been selected by the operator (previously "discounts")
         * This overrides all other offers for the line.
         *
         * @type {Offer|null}}
         */
        this.manualOffer = null;

        /**
         * All of the offers/discounts affecting this line.
         *
         * @type {CartItemDiscount[]}
         */
        this.discounts = [];

        /**
         * If the price gets adjusted due to chargeable product options remember that.
         * This is so that we can undo changes to the price when appropriate.
         *
         * @type {boolean}
         */
        this.priceIncludesChargeableProductOptions = false;

        /**
         * If the operator entered a custom price for this item remember it here.
         *
         * @type {?Number}
         */
        this.customPrice = null;

        /**
         * The total percentage of discount for the line (calculated for amount discounts).
         *
         * @type {number}
         */
        this.discountPercentage = 0.00;

        /**
         * The base total after discounts are deducted, before fees.
         * This is used for tax purposes, as the fees are taxed independently.
         * (price * qty) - (discountAmount)
         * (baseTotal) - (discountAmount)
         *
         * @type {number}
         */
        this.discountedBaseTotal = 0;

        /**
         * The price after discounts are deducted, before fees.
         * This is used for tax purposes, as the fees are taxed independently.
         *
         * @type {number}
         */
        this.discountedPrice = 0;

        /**
         * Member / address data for membership(s) on this line specifically for refunds. Normal membership datasets are
         * held on the instances, but we don't have instances for refund items.
         *
         * @type {MembershipDataset[]}
         */
        this.membershipDatasetsForRefunds = [];

        /**
         * Gift Aid price in use?
         *
         * @type {boolean}
         */
        this.giftAid = false;

        /**
         * @type {number}
         */
        this.donation = 0.00;

        /**
         * Allow the user to adjust the qty of this line in the cart?
         *
         * @type {boolean}
         */
        this.canAdjustQuantity = true;

        /**
         * @type {number|null}
         */
        this.minimumQuantity = null;

        /**
         * Allow the user to remove this line from the cart?
         *
         * @type {boolean}
         */
        this.canRemove = true;

        /**
         * The line number given to this cart item by the cart. Starting from 1.
         *
         * Used to link custom field data to the line, in conjunction with
         * item instances.
         *
         * FIXME: This gets set when you call CartService.add(), but cart lines may be created other ways
         * and this may not be set.
         *
         * @type {number}
         */
        this.lineNumber = 0;

        /**
         * Used when refunding a membership.
         * Determines how much of the value of this line is being apportioned to which membership subscriptions.
         * It should be an object in the format:
         * {
         *     instanceNumber: {
         *         membershipSubscriptionId: 123.00,
         *         membershipSubscriptionId: 456.00
         *     }
         * }
         *
         * @type {null|Object<Object<number>>}
         */
        this.membershipSubscriptionApportionedAmounts = null;

        /**
         * This will be populated if this line is a refund for a membership that will be cancelled.
         * The membership will be cancelled by the SellCtrl when the user clicks the pay button.
         *
         * @type {Membership|null}
         */
        this.cancellingMembership = null;

        /**
         * This will be populated if this line is renewing a membership.
         *
         * @type {Membership|null}
         */
        this.renewingMembership = null;

        /**
         * @type {Date|null}
         */
        this.renewingMembershipFromDate = null;

        /**
         * @type {SoldGiftVoucherData|null}
         */
        this.soldGiftVoucherData = null;

        /**
         * Array of discount reasons that aren't really discounts they are adjustments made by specific payment types
         * for example Experience Vouchers with tax on sale
         *
         * @type {string[]}
         */
        this.paymentDiscountReasons = [PaymentMethodRef.GIFT_VOUCHER];

        /**
         * If this cart line was built from an existing OrderLine, remember the ID of that orderLine.
         *
         * @type {?Number}
         */
        this.ordersItemsID = null;

        /**
         * If this cart line was built from an existing OrderLine this will be the number of times that line has
         * already been redeemed.
         *
         * @type {number|null}
         */
        this.redeemed = null;

        /**
         * @type {null}
         */
        this.contentHashInitial = null;

        /**
         * Collection of VariableDiscount objects containing amounts and narratives of variable discount offers.
         *
         * @type {?VariableDiscountsCollection}
         */
        this.variableDiscountsCollection = null;

        /**
         * What is the edit state of a line?
         * We can only edit Tickets that have not been redeemed and which have not expired.
         *
         * @type {string}
         */
        this.editableStatus = ItemEditableStatus.IS_EDITABLE;

        /**
         * Has stock been checked for this product and there is insufficient stock for the request?
         *
         * @type {boolean}
         */
        this.hasStockLevelError = false;

        /**
         * @type {ErrorBag}
         */
        this.errors = new ErrorBag();

        /**
         * If this line came from an order we are adjusting, these are the original OrderLine(s) it came from.
         * This is an array because in orders the discounted lines get split up, but in ProPoint they are handled
         * as a single line.
         *
         * @type {OrderLine[]}
         */
        this.originalOrderLines = [];

        /**
         * A unique identifier for this line.
         * This will be sent to the API, and can be used to identify the line when creating the reservation and order.
         *
         * @type {string}
         */
        this.thirdPartyRef = crypto.randomUUID();

        /**
         * If this line is the child of another line, this will be the thirdPartyRef of the parent line.
         *
         * @type {string|null}
         */
        this.parentThirdPartyRef = null;

        /**
         * Contains references to any other lines from the cart that are children of this line.
         * This is updated when the cart is calculated.
         *
         * @type {CartLine[]}
         */
        this.childLines = [];

        /**
         * Total of the lineTotal from all the ItemOption lines references by this.optionLines.
         *
         * @type {number}
         */
        this.optionLinesTotal = 0.00;

        if (cart) {
            this.setCart(cart);
        }

        this.calculate();

        /**
         * Determines if one of the adding/modifying/removing/returning badges should be displayed on this line
         * when viewed in the cart.
         *
         * @see {CartLineUiState}
         *
         * @type {string|null} state-adding|state-removing|state-changing
         */
        this.lineUiState = null;

        /**
         * The optional group booking reference that has been assigned to this line.
         *
         * @type {string|null}
         */
        this.groupBookingReference = null;
    }

    get membershipDatasets() {
        // Returns a combined array of datasets from the item instances and membershipDatasetsForRefunds which can't be
        // stored on item instances.
        return [...this.getMembershipDatasetsFromItemInstances(), ...this.membershipDatasetsForRefunds];
    }

    /**
     * Adjust the quantity of this line up or down by the amount provided.
     *
     * !!! NOTE
     * Do not call this method directly if the line is already in a Cart.
     * You can use this method to set up a line before adding it to a Cart, but once it is in a Cart adjusting
     * the line directly will leave the Cart in an inconsistent state.
     * Instead use CartService.adjustCartLineQuantity
     *
     * @param {Number} adjustQuantityBy
     *
     * @returns {CartLine}
     */
    adjustQuantity(adjustQuantityBy) {
        let newQty = this.quantity + parseInt(adjustQuantityBy, 10);
        if (newQty === 0 && adjustQuantityBy > 0) {
            newQty++; // make the quantity 1 not zero if increasing from minus
            adjustQuantityBy++; // adjust the adjustment by the same ++
        }

        // If we are adding a new line as part of an order edit then clearly the quantity cannot be <= 0.
        if (
            this.cart
            && this.cart.currentCart
            && this.cart.currentCart.isEditingOrder
            && newQty === 0
            && this.qtyBeforeAdjustment === 0
        ) {
            newQty = 1;
        }

        this.setQuantity(newQty);
        this.quantityDisplay = this.quantity;

        this.lineUiState = calculateCartLineUiState(this);

        return this;
    }

    /**
     * @param {number} instanceNumber
     *
     * @return {?ItemInstance}
     */
    getInstance(instanceNumber) {
        return this.itemInstances.find((i) => i.instance === instanceNumber) || null;
    }

    /**
     * @param {number} instanceNumber
     *
     * @return {?ItemInstance} Returns the removed instance.
     */
    removeInstance(instanceNumber) {
        let instance = this.getInstance(instanceNumber);
        if (instance) {
            let index = this.itemInstances.indexOf(instance);
            if (index !== -1) {
                this.itemInstances.splice(index, 1);
                return instance;
            }
        }

        return null;
    }

    setCanAdjustQuantity(value) {
        this.canAdjustQuantity = value;
    }

    setCanRemove(value) {
        this.canRemove = value;
    }

    validateQuantityChange() {
        if (!(/^-?[1-9]\d{0,2}$/.test(this.quantityDisplay))) {
            return false;
        }

        if (this.minimumQuantity !== null && this.quantityDisplay < this.minimumQuantity) {
            return false;
        }

        return true;
    }

    /**
     * @param {boolean} [calculateCart] Should this calculation trigger a calculation of the cart? Default is true.
     *
     * @return {CartLine}
     */
    calculate(calculateCart) {
        cleanCartLine(this);

        // Adjust the 'price' field of this line to include any chargeable product options.
        // This probably won't work well with session-based prices for tickets, because
        // that also modifies the 'price' field. However, chargeable production options should only be used
        // on products not on tickets so it shouldn't be an issue because
        // this is supposed to be a "temporary solution" 😉
        // Hey whadya know - it was a temporary solution and only lasted 1 year! Now it's being replaced :D
        const chargeableOptionsTotal = this.getPriceOfOptions();
        if (chargeableOptionsTotal !== 0) {
            // There are some chargeable product options to add to the price.
            this.price = new BigNumber(this.item.initialPrice)
                .plus(chargeableOptionsTotal)
                .toNumber();
            this.priceIncludesChargeableProductOptions = true;
        } else if (this.priceIncludesChargeableProductOptions) {
            // There were some chargeable product options in the price but these are no longer there.
            // Reset the price to the item's initial price.
            this.price = this.item.initialPrice;
            this.priceIncludesChargeableProductOptions = false;
        }

        let baseTotal = (new BigNumber(this.price)).multipliedBy(this.quantity);

        this.baseTotal = baseTotal.toNumber();

        this.bookingFeeSubtotal = (new BigNumber(this.bookingFee)).multipliedBy(this.quantity).toNumber();

        // Calculate the total amount of discount provided by all discounts on this line.
        let discountAmount = new BigNumber(0);
        let discountedPrice = new BigNumber(0);
        let discountedQuantity = new BigNumber(0);

        for (let i = 0; i < this.discounts.length; i++) {
            let discount = this.discounts[i];

            if (discount.isActive()) {
                let amount;
                if (discount.isVariable()) {
                    amount = this.getTotalDiscountAmount(discount);
                } else {
                    amount = discount.amount;
                }
                discountAmount = discountAmount.plus(amount);

                discountedQuantity = discountedQuantity.plus(discount.qty);
            }
        }

        // Set total amount of discount for the whole line.
        this.discountAmount = discountAmount.toNumber();

        this.discountedQuantity = discountedQuantity.toNumber();

        // Calculate total discount percentage for the whole line.
        // This applies to Experience vouchers tax on sale but not tax on use.
        let discountPercentage = new BigNumber(0.00);
        if (discountAmount.isGreaterThan(0)) {
            discountPercentage = discountAmount.multipliedBy(100).dividedBy(baseTotal);
        }
        this.discountPercentage = discountPercentage.toNumber();

        // This allows us to add a discount to a zero value item.
        if (this.discountPercentage === Infinity) {
            this.discountPercentage = 0;
        }

        let discountedBaseTotal = baseTotal.minus(discountAmount);
        this.discountedBaseTotal = discountedBaseTotal.toNumber();

        const discountDecimal = new BigNumber(100).minus(this.discountPercentage).dividedBy(100);
        discountedPrice = discountedPrice.plus(this.getPrice()).multipliedBy(discountDecimal);
        this.discountedPrice = discountedPrice.toNumber();

        this.lineTotal = discountedBaseTotal.plus(this.bookingFeeSubtotal).toNumber();

        // Add up the total of the option lines.
        this.optionLinesTotal = this.optionLines.reduce((sum, line) => {
            return sum.plus(line.lineTotal);
        }, new BigNumber(0)).toNumber();

        if (calculateCart !== false && this.cart) {
            this.cart.calculate();
        }
        return this;
    }

    /**
     * @param {CartItemDiscount} itemDiscount
     */
    addDiscount(itemDiscount) {
        this.discounts.push(itemDiscount);
        this.discounts = [...new Set(this.discounts)];
        this.sortDiscounts();
    }

    /**
     * @param {SoldGiftVoucher} soldGiftVoucher
     */
    assignSoldGiftVoucher(soldGiftVoucher) {
        this.soldGiftVoucherData = Object.assign(new SoldGiftVoucherData(), {
            deliveryType: soldGiftVoucher.deliveryType,
            recipientContact: Object.assign(new Contact(), soldGiftVoucher.recipient),
            recipientMessage: soldGiftVoucher.recipientMessage,
        });
    }

    /**
     * @param {CartItemDiscount[]} itemDiscounts
     */
    setDiscounts(itemDiscounts) {
        this.discounts = itemDiscounts;
        this.sortDiscounts();
    }

    sortDiscounts() {
        this.discounts.sort(
            function (a, b) {
                // Was the discount added to the line by the operator clicking the pencil/edit button?
                // This can be a predetermined discount or a variable discount.
                if (a.manual !== b.manual) {
                    return (a.manual) ? -1 : 1;
                }

                if (a.amount > b.amount) {
                    return -1;
                } else if (a.amount < b.amount) {
                    return 1;
                }

                return 0;
            }
        );
    }

    /**
     * @returns {CartItemDiscount[]}
     */
    getDiscounts() {
        return this.discounts;
    }

    /**
     * @return {?CartItemDiscount|null}
     */
    getFirstActiveDiscount() {
        return this.discounts.filter((d) => !!d.active)[0] || null;
    }

    /**
     * Get the total discount amount for the line provided by the given discount.
     *
     * @param {CartItemDiscount} discount
     *
     * @returns {number}
     */
    getTotalDiscountAmount(discount) {
        // Check for variable discounts, but make sure we exclude matching experience vouchers.
        if (discount.offer && discount.isVariable() && this.variableDiscountsCollection) {
            let variableDiscount = this.variableDiscountsCollection.getVariableDiscount(discount.offerID);
            if (variableDiscount) {
                return new BigNumber(variableDiscount.amount)
                    .multipliedBy(this.quantity)
                    .toNumber();
            }
        }

        return discount.amount;
    }

    /**
     * Get the discount amount for each item on the line provided by the given discount.
     *
     * @param {CartItemDiscount} discount
     *
     * @returns {number}
     */
    getDiscountAmountPerItem(discount) {
        return new BigNumber(this.getTotalDiscountAmount(discount))
            .dividedBy(discount.qty)
            .toNumber();
    }

    /**
     * Return the narrative for a VariableDiscount
     *
     * @returns {string|null}
     */
    getVariableDiscountNarrative() {
        let discounts = this.getDiscounts();

        for (let discount of discounts) {
            if (discount.isVariable() && discount.offerID && this.variableDiscountsCollection) {
                let variableDiscount = this.variableDiscountsCollection.getVariableDiscount(discount.offerID);
                if (variableDiscount) {
                    return variableDiscount.narrative;
                }
            }
        }
        return null;
    }

    /**
     * Clear discounts
     */
    clearDiscounts() {
        this.discounts = [];
    }

    /**
     * Clear all active discounts but leave inactive ones.
     */
    clearActiveDiscounts() {
        this.discounts = this.discounts.filter((d) => !d.active);
    }

    /**
     * Clear discounts that came from the cart calculations API (order level offers and gift vouchers),
     * leaving manual line offers selected by the operator.
     */
    clearNonManualDiscounts() {
        this.discounts = this.discounts.filter((d) => d.manual);
    }

    /**
     * Clear discounts that are really cost adjustments for example those created by tax on sale Experience Gift
     * vouchers. These need to be specifically removed when the payment modal is cancelled and abandoned.
     */
    clearPaymentDiscounts() {
        this.discounts = this.discounts.filter((discount) => !this.paymentDiscountReasons.includes(discount.reason));
    }

    /**
     * Set the offer that was chosen by the operator.
     *
     * @param {Offer|null} offer
     */
    setManualOffer(offer) {
        this.manualOffer = offer;
        this.calculate();
    }

    /**
     * @returns {Offer|null}
     */
    getManualOffer() {
        return this.manualOffer;
    }

    /**
     * For session based tickets only return true if there is an availability problem.
     *
     * @returns {boolean}
     */
    hasAvailabilityError() {
        if (this.session == null) {
            return false;
        }

        return this.getCartSessionRequiredSpacesQuantity() > this.session.spacesAvailable;
    }

    /**
     * This method is not currently used.
     *
     * @return {boolean}
     */
    hasDiscounts() {
        return this.discounts.length > 0;
    }

    hasDiscountsExcludingExperienceVouchers() {
        return this.discounts.filter((discount) => discount.reason !== PaymentMethodRef.GIFT_VOUCHER).length > 0;
    }

    hasSoldGiftVoucher() {
        return !!(this.itemInstances.find((i) => i.soldGiftVoucher));
    }

    /**
     * @private
     *
     * @return {object}
     */
    getIDComponents() {
        const components = {
            branchID: this.branch.ID,
            itemID: this.getItemID(),
            price: this.price,
            bookingFee: this.bookingFee,
            giftAid: this.giftAid,
            isRefund: this.getIsRefund(),
            isPriceAdjusted: this.price !== this.item.getPrice(),
            manualOfferID: this.manualOffer ? this.manualOffer.ID : null,
            // Trigger may seem superfluous, but it allows us to distinguish between lines that have discounts applied
            // in backoffice which do not have actual offers, just the discount. Checking trigger lets us distinguish
            // between lines of the same item which have these discounts applied and those that do not.
            // @see mapOrderToCart.js
            // @see mergeDiscountedCartLines.js
            manualOfferTrigger: this.manualOffer ? this.manualOffer.trigger : null,
            sessionID: this.getSessionID(),
            renewingMembershipID: this.renewingMembership ? this.renewingMembership.ID : null,
            cancellingMembershipID: this.cancellingMembership ? this.cancellingMembership.ID : null,
            groupBookingReference: this.groupBookingReference,
        };

        if (this.parentThirdPartyRef) {
            components.parentThirdPartyRef = this.parentThirdPartyRef;
        }

        if (this.optionSet) {
            // Don't merge item option selection lines that are answering different option sets.
            components.optionSetID = this.optionSet.ID;
        }

        return components;
    }

    /**
     * Create a unique ID for the line by using JSON.stringify() on object containing properties we want to compare.
     * This is used to determine which lines can be merged together by increasing the qty of one rather than adding
     * a new line.
     *
     * @return {string}
     */
    getID() {
        let components = {
            ...this.getIDComponents(),
            // Value of new style ask early field data. Until the user can change the new field data the value
            // will always be null or the same for every line with the same fields so it won't affect anything,
            askEarlyFieldHash: this.getAskEarlyFieldHash(),
            editableStatus: this.editableStatus,
        };

        const itemOptionSelections = this.getItemOptionSelections();
        if (Object.keys(itemOptionSelections).length > 0) {
            components.itemOptionSelections = itemOptionSelections;
        }

        if ([
            ItemType.MEMBERSHIP_PLAN,
            ItemType.GIFT_VOUCHER,
        ].includes(this.getItemType())) {
            // This line contains an item type that we never want to group together.
            // Make this line's ID unique so nothing gets merged with it.
            components.thirdPartyRef = this.thirdPartyRef;
        }

        return JSON.stringify(components);
    }

    /**
     * The getID method is used to generate a unique identifier by using JSON.stringify() on an object containing
     * properties for the line that we want to compare, so we can see "what is being sold at what price" so we know what
     * lines are equivalent  and could be merged together. This also uses JSON.stringify(), but it includes more
     * properties like the qty of the line and the values of fields to determine "has this line been changed during
     * adjusting an order". The value of this when the line is first created from an order can be compared with now to
     * see if anything has changed. This is a more reliable way of determining if the line has changed instead of trying
     * to set a flag to say it has changed.
     */
    getContentHash() {
        return md5Hash(JSON.stringify(this.getContentObject()));
    }

    getContentObject() {
        let result = {
            ...this.getIDComponents(),
            quantity: this.quantity,
            fieldHash: this.getFieldValuesHash(),
            ...this.getDiscountDataForHash(),
        };

        return result;
    }

    /**
     * If this line contains a refund then if we add another instance of the same item this should go on a new line.
     *
     * A refund line is easy to detect when creating a new order - it is simply qty < 0, however in an order adjustment
     * this is indicated by qty < qtyBeforeAdjustment. When editing an order it is ok if we increment qty that has
     * previously been adjusted downwards as long as there is no returnReasonID.
     *
     * @returns {boolean}
     */
    getIsRefund() {
        if (this.quantity < 0) {
            return true;
        }
        if (this.quantity < this.qtyBeforeAdjustment && this.returnReason.reasonID) {
            return true;
        }
        return false;
    }

    /**
     * @returns {Number|null}
     */
    getItemID() {
        return this.item ? this.item.ID : null;
    }

    getItemType() {
        return this.item ? this.item.itemType : null;
    }

    isGiftVoucher() {
        return this.getItemType() === ItemType.GIFT_VOUCHER;
    }

    isMembershipPlan() {
        return this.getItemType() === ItemType.MEMBERSHIP_PLAN;
    }

    isGiftAidable() {
        return this.item.giftAid;
    }

    getCategoryID() {
        return this.item && this.item.category ? this.item.category.ID : null;
    }

    /**
     * @return {?SessionEvent}
     */
    getEvent() {
        return this.item && this.item.event ? this.item.event : null;
    }

    /**
     * @return {number|null}
     */
    getEventID() {
        return this.item && this.item.event ? this.item.event.ID : null;
    }

    /**
     * @return {number|null}
     */
    getSessionID() {
        return this.session ? this.session.ID : null;
    }

    /**
     * @return {string|null}
     */
    getSessionFullName() {
        return this.session ? this.session.getFullName() : null;
    }

    /**
     * @return {string}
     */
    getName() {
        if (this.giftAid) {
            return this.item.getName() + ' (Gift Aid)';
        }
        return this.item.getName();
    }

    /**
     * @return {string}
     */
    getNameWithQty() {
        return `${this.quantity} x ${this.getName()}`;
    }

    setSubtitle(subtitle) {
        this.subtitle = subtitle;
    }

    getPrice() {
        return this.price;
    }

    /**
     * Returns the sum of any options on the line that have a price attached to them.
     *
     * @return {number}
     */
    getPriceOfOptions() {
        return getPriceForOptionsOnLine(this);
    }

    hasChangedPrice() {
        return this.price !== this.item.initialPrice;
    }

    /**
     * Is the total of this line more than zero?
     * i.e. it's not a free item, and it's not a refund.
     *
     * @returns {boolean}
     */
    isPositiveValue() {
        return this.getLineTotal() > 0;
    }

    getDonation() {
        return this.donation;
    }

    getQuantity() {
        return this.quantity;
    }

    /**
     * Returns the difference between the current quantity and the quantity before any adjustments were made.
     * For new lines qtyBeforeAdjustment will be 0 so this will always be the quantity.
     *
     * @returns {number}
     */
    getQuantityDifference() {
        return this.quantity - this.qtyBeforeAdjustment;
    }

    /**
     * Get the total quantity of new spaces required for this session on all tickets in the cart. Note this ignores
     * quantities that have already been purchased if this is an order edit.
     *
     * @return {number|null}
     */
    getCartSessionRequiredSpacesQuantity() {
        return this.cart.getCartSessionRequiredSpacesQuantity(this.session.ID);
    }

    /**
     * Return the total number of people on this line.
     *
     * @returns {number|null}
     */
    getPeople() {
        if (
            this.item instanceof MembershipPlan
            || this.item instanceof Ticket
        ) {
            return new BigNumber(this.getQuantity())
                .multipliedBy(this.item.getPeople())
                .toNumber();
        }

        return null;
    }

    getBaseTotal() {
        return this.baseTotal;
    }

    getDiscountedBaseTotal() {
        return this.discountedBaseTotal;
    }

    getDiscountedPrice() {
        return this.discountedPrice;
    }

    getLineTotal() {
        return this.lineTotal;
    }

    /**
     * Returns the price difference between this cart line's total as it currently stand and the total from any
     * original order lines that this cart line came from.
     *
     * @return {number}
     */
    getLineTotalDifferenceFromOriginal() {
        let originalLinesTotal = new BigNumber(0);
        this.originalOrderLines.forEach((orderLine) => {
            originalLinesTotal = originalLinesTotal.plus(orderLine.lineTotal);
        });

        return new BigNumber(this.getLineTotal()).minus(originalLinesTotal).toNumber();
    }

    setPrice(price) {
        this.price = price;
        this.calculate();
        return this;
    }

    /**
     * Set the quantity of this line to a new absolute value.
     *
     * !!! NOTE
     * Do not call this method directly if the line is already in a Cart.
     * You can use this method to set up a line before adding it to a Cart, but once it is in a Cart adjusting
     * the line directly will leave the Cart in an inconsistent state.
     * Instead use CartService.setCartLineQuantity
     *
     * @param {number} newQuantity
     * @param {boolean} [calculateCart]
     *
     * @return {CartLine}
     */
    setQuantity(newQuantity, calculateCart = true) {
        const previousQuantity = this.quantity;
        this.quantity = newQuantity;
        this.quantityDisplay = newQuantity;

        // Use the clean function to create/remove item instances as necessary and fix field data numbering.
        cleanCartLine(this);

        // If we are returning an item in a new order the quantity will always be -ve. So if we have set a returnReason,
        // clear this when quantity > 0 (i.e. operator has changed their mind about it being a return).
        // But if we are returning an item as part of an order edit then quantity will always be >= 0. Only clear the
        // returnReason when the quantity is >= qtyBeforeAdjustment. This allows the operator to adjust the qty without
        // having to add the returnReason again if they need to adjust the number of items that are being returned.
        if (
            (this.quantity > 0 && !this.isEditingLine())
            || (this.quantity >= this.qtyBeforeAdjustment && this.isEditingLine())
        ) {
            this.returnReason.reasonID = null;
            this.returnReason.returnToStock = null;
        }

        // The API never discounts negative lines, so discounts it returned while this line was a sale would
        // increase the refund. Manual line offers are recalculated from the new quantity so they are kept.
        if (previousQuantity > 0 && newQuantity < 0) {
            this.clearNonManualDiscounts();
        }

        this.calculate(calculateCart);

        return this;
    }

    /**
     * Enable Gift Aid for this item. Adjust the price as appropriate.
     */
    enableGiftAid() {
        if (!this.item.giftAid || !this.item.giftAidRate) {
            // Gift Aid not available on this item.
            return false;
        }

        if (this.giftAid) {
            // Gift Aid already enabled.
            return false;
        }

        this.giftAid = true;
        this.useGiftAidRateDonation();

        return true;
    }

    useGiftAidRateDonation() {
        let originalPrice;
        if (this.customPrice !== null) {
            originalPrice = this.customPrice;
        } else {
            originalPrice = this.item.getPrice();
        }

        const priceAdjustmentCalculator = new PriceAdjustmentCalculator();
        let { newPrice } = priceAdjustmentCalculator.calculateAdjustedPrice(
            this.item.giftAidRate,
            Number(originalPrice)
        );

        this.setPrice(newPrice);
        this.donation = (new BigNumber(newPrice)).minus(originalPrice).toNumber();
    }

    disableGiftAid() {
        this.giftAid = false;
        this.removeDonation();
    }

    removeDonation() {
        if (!this.donation) {
            return;
        }

        // We can get back to the original price by subtracting the donation for the current price.
        let originalPrice = (new BigNumber(this.price)).minus(this.donation).toNumber();
        this.setPrice(originalPrice);
        this.donation = 0.00;
    }

    /**
     * Return a shallow copy of this CartItem
     *
     * @return {CartLine}
     */
    clone() {
        return cloneShallow(this);
    }

    /**
     * @return {?string}
     */
    getAskEarlyFieldHash() {
        if (!this.itemInstances[0]) {
            return null;
        }

        let askEarlyInstances = this.itemInstances[0].fields.instances
            .filter(FieldInstanceFilter.ASK_EARLY_FIELDS_ONLY);

        return getHashOfFieldValues(askEarlyInstances);
    }

    /**
     * @return {string|null}
     */
    getFieldValuesHash() {
        let fieldInstances = getAllFieldInstancesFromGroups(getAllFieldGroupsFromLines([this]));

        return getHashOfFieldValues(fieldInstances);
    }

    /**
     * Returns the currently selected options for a cart line.
     *
     * This relies on this.optionLines being kept up to date.
     *
     * @returns {OptionSelections}
     */
    getItemOptionSelections() {
        return convertItemOptionLinesToOptionSelections(this.optionLines);
    }

    /**
     * The discount elements have been moved to their own method in case it becomes necessary to implement a more
     * complex hash of the actual discounts array in the way we do for field data.
     *
     * @return {object}
     */
    getDiscountDataForHash() {
        return {
            discountAmount: this.discountAmount,
            discountPercentage: this.discountPercentage,
            discountedBaseTotal: this.discountedBaseTotal,
            discountedPrice: this.discountedPrice,
            discountedQuantity: this.discountedQuantity,
        };
    }

    /**
     * @param {CartService} cart
     */
    setCart(cart) {
        this.cart = cart;
        this.calculate();
    }

    /**
     * @return {boolean}
     */
    isRepeatingPayment() {
        return this.item.paymentPattern !== PaymentPatterns.SINGLE;
    }

    /**
     * Set the amount being refunded to membership subscriptions by this line.
     *
     * These amounts are calculated by the 'membership refund amounts' API endpoint
     * (use MembershipService.calculateMembershipRefunds())
     *
     * It should be an object where the keys are membership subscription IDs, and the values
     * are the amount (in currency) that should be apportioned to that membership subscription from the payment(s)
     * for this order.
     * This is only currently used for refunding memberships, so the amounts should all be negative.
     *
     * @param {Object<Object<number>>} amounts
     */
    setMembershipSubscriptionApportionedAmounts(amounts) {
        this.membershipSubscriptionApportionedAmounts = amounts;
    }

    /**
     * If this line is a refund for a membership, that membership should not have been cancelled yet.
     * Setting the membership here will cause the SellCtrl to trigger to actual cancellation of this membership
     * once the refund has been completed.
     *
     * @param {Membership|null} membership
     */
    setCancellingMembership(membership) {
        this.cancellingMembership = membership;
        if (membership) {
            this.setSubtitle('Refund for Membership ' + membership.getRef());
        } else {
            this.setSubtitle(null);
        }
    }

    /**
     * This method is only used by the MembershipService when making a refund. It adds the dataset directly to the Item
     * rather than to the ItemInstance because -ve qty items do not have instances.
     *
     * @param {MembershipDataset} membershipDatasetForRefund
     */
    addMembershipDatasetForRefund(membershipDatasetForRefund) {
        this.membershipDatasetsForRefunds.push(membershipDatasetForRefund);
    }

    /**
     * @return {MembershipDataset[]}
     */
    getMembershipDatasetsFromItemInstances() {
        let membershipDatasets = [];
        this.itemInstances.forEach((itemInstance) => {
            if (itemInstance.membershipDataset) {
                membershipDatasets.push(itemInstance.membershipDataset);
            }
        });
        return membershipDatasets;
    }

    /**
     * Sets the membershipDataset on each item instance in order and sets this.membershipDatasets for ease of use in
     * templates.
     *
     * Note: we need to keep this.membershipDatasets rather than just using a getter or calling
     * this.getMembershipDatasets() because only positive qty CartLines have instances.
     *
     * @param {MembershipDataset[]} membershipDatasets
     */
    setMembershipDatasets(membershipDatasets) {
        for (let i = 0; i < this.itemInstances.length; i++) {
            this.itemInstances[i].membershipDataset = membershipDatasets[i];
        }
    }

    /**
     * @param {Membership|null} membership
     * @param {Date} fromDate
     */
    setRenewingMembership(membership, fromDate) {
        this.renewingMembership = membership;
        this.renewingMembershipFromDate = fromDate;

        if (membership) {
            let subtitle = 'Renewing Membership ' + membership.getRef();
            if (fromDate) {
                subtitle += ' from ' + moment(fromDate).format('YYYY-MM-DD');
            }

            this.setSubtitle(subtitle);
        } else {
            this.setSubtitle(null);
        }
    }

    /**
     * @param {VariableDiscountsCollection} variableDiscountsCollection
     */
    setVariableDiscountsCollection(variableDiscountsCollection) {
        this.variableDiscountsCollection = variableDiscountsCollection;
    }

    getHydrationMap() {
        return {
            baseTotal: toFloat,
            bookingFee: toFloat,
            bookingFeeSubtotal: toFloat,
            branch: {
                model: Branch,
            },
            canAdjustQuantity: toBool,
            cancellingMembership: {
                model: Membership,
            },
            canRemove: toBool,
            contentHashInitial: toNullableString,
            customPrice: toNullableFloat,
            discountAmount: toFloat,
            discountedBaseTotal: toFloat,
            discountedPrice: toFloat,
            discountedQuantity: toInt,
            discountPercentage: toFloat,
            discounts: {
                modelCollection: CartItemDiscount,
            },
            donation: toFloat,
            errors: ErrorBag,
            editableStatus: toString,
            giftAid: toBool,
            groupBookingReference: toNullableString,
            hasStockLevelError: toBool,
            item: {
                modelFunc(data) {
                    if (data.item && data.item.itemType) {
                        return mapItemTypeToModel(data.item.itemType);
                    }
                    if (data.itemType) {
                        return mapItemTypeToModel(data.itemType);
                    }
                    return null;
                },
            },
            itemInstances: {
                modelCollection: ItemInstance,
            },
            lineNumber: toInt,
            lineTotal: toFloat,
            lineUiState: toNullableString,
            manualOffer: {
                model: Offer,
            },
            membershipDatasetsForRefunds: {
                modelCollection: MembershipDataset,
            },
            membershipSubscriptionApportionedAmounts: {},
            minimumQuantity: toNullableInt,
            optionSet: {
                model: OptionSet,
            },
            ordersItemsID: toNullableInt,
            originalOrderLines: {
                modelCollection: OrderLine,
            },
            parentThirdPartyRef: toNullableString,
            price: toFloat,
            priceIncludesChargeableProductOptions: toBool,
            qtyBeforeAdjustment: toInt,
            quantity: toInt,
            redeemed: toNullableInt,
            removedOriginalItemInstances: {
                modelCollection: ItemInstance,
            },
            renewingMembership: {
                model: Membership,
            },
            renewingMembershipFromDate: toDate,
            returnReason: {},
            session: {
                model: Session,
            },
            soldGiftVoucherData: {
                model: SoldGiftVoucherData,
            },
            subtitle: toNullableString,
            thirdPartyRef: toString,
            variableDiscountsCollection: {
                model: VariableDiscountsCollection,
            },
        };
    }

    afterHydration() {
        // quantityDisplay isn't imported because it's an implementation detail not really a piece of data about
        // the cart. It should almost always be the same as this.quantity.
        this.quantityDisplay = this.quantity;

        if (this.hasSoldGiftVoucher()) {
            this.assignSoldGiftVoucher(this.itemInstances[0].soldGiftVoucher);
        }
    }

    /**
     * @return {boolean}
     */
    isEditable() {
        return this.editableStatus === ItemEditableStatus.IS_EDITABLE;
    }

    /**
     * Existing lines that are part of an order edit will contain the ordersItemsID of the original line.
     *
     * @returns {boolean}
     */
    isEditingLine() {
        return !!this.ordersItemsID;
    }

    /**
     * Compare the contents hash now vs the contentHashInitial (if there is one).
     *
     * Returns null if there is no contentHashInitial.
     *
     * @return {null|boolean}
     */
    isAdjusted() {
        if (!this.contentHashInitial) {
            return null;
        }
        return this.contentHashInitial !== this.getContentHash();
    }

    /**
     * For compatibility with OrderLine.
     *
     * @return {ItemInstance[]}
     */
    getItemInstances() {
        return this.itemInstances;
    }

    /**
     * Returns the itemInstanceID of all instances on the line.
     * Only instances that came from an existing order will have an ID. New instances will not.
     *
     * @return {number[]}
     */
    getItemInstanceIDs() {
        return this.itemInstances
            .filter((itemInstance) => !!itemInstance.ID)
            .map((itemInstance) => itemInstance.ID);
    }

    /**
     * @returns {boolean}
     */
    hasBookableResources() {
        return this.item.hasBookableResources;
    }

    /**
     * @returns {boolean}
     */
    get isChild() {
        return !!this.parentThirdPartyRef;
    }

    /**
     * @returns {boolean}
     */
    get isItemOption() {
        return this.getItemType() === ItemType.ITEM_OPTION;
    }

    /**
     * Returns a filtered list of this.childLines containing only
     * option lines that are children of this line.
     *
     * @returns {CartLine[]}
     */
    get optionLines() {
        return this.childLines.filter((l) => l.isItemOption && l.optionSet);
    }

    /**
     * @return {string|null}
     */
    get paymentDescription() {
        return this.item?.paymentDescription || null;
    }

    /**
     * Returns the absolute qty on the line, ignoring if this is a sale or refund.
     *
     * @return {number}
     */
    get absQuantity() {
        return Math.abs(this.quantity);
    }
}

module.exports = CartLine;
